import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { usePlatformStore } from '../../stores/platform.store';

type LoginInput = { email: string; password: string };

interface LoginResponse {
  status: 'OK' | 'MFA_REQUIRED' | 'PLATFORM_OK' | 'EMAIL_OTP_REQUIRED' | 'EMAIL_VERIFICATION_REQUIRED';
  user?: { id: string; email: string };
  session?: string;
  email?: string;
  /** T-MAIL — e-posta OTP challenge (ilk-giriş doğrulama / yetkili MFA). */
  challenge?: string;
  purpose?: 'FIRST_LOGIN_VERIFY' | 'LOGIN_MFA';
  /** Platform personeli login akışı (status === 'PLATFORM_OK'). */
  mfa_setup_required?: boolean;
  staff?: { id: string; email: string; name: string; mfaEnabled: boolean };
}

export function LoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const setUser = useAuthStore((s) => s.setUser);
  const fetchSession = useAuthStore((s) => s.fetchSession);
  const fetchPlatformSession = usePlatformStore((s) => s.fetchPlatformSession);
  const [submitting, setSubmitting] = useState(false);

  const loginSchema = useMemo(
    () =>
      z.object({
        email: z.string().email(t('login.validation.email')),
        password: z.string().min(8, t('login.validation.password')),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({ resolver: zodResolver(loginSchema) });

  async function onSubmit(input: LoginInput) {
    setSubmitting(true);
    try {
      const result = await api.post<LoginResponse>('/auth/login', input);

      if (result.status === 'MFA_REQUIRED') {
        navigate('/mfa', { state: { email: result.email, session: result.session } });
        return;
      }

      // T-MAIL — ilk-giriş e-posta doğrulama veya yetkili login MFA (e-posta OTP).
      if (result.status === 'EMAIL_OTP_REQUIRED') {
        navigate('/dogrulama', {
          state: { email: result.email, challenge: result.challenge, purpose: result.purpose },
        });
        return;
      }

      // Parola doğru ama e-posta doğrulanmamış → doğrulama akışına yönlendir (kod e-postalandı).
      if (result.status === 'EMAIL_VERIFICATION_REQUIRED') {
        // Login backend'i zaten bir doğrulama kodu yolladı → sayfa mount'ta tekrar göndermesin.
        navigate('/eposta-dogrula', { state: { email: result.email, codeAlreadySent: true } });
        return;
      }

      // Platform (Custel operatör) personeli — ayrı konsola yönlendir.
      if (result.status === 'PLATFORM_OK') {
        // GEÇİCİ BYPASS — MFA setup akışı bozuk (bkz. bug tracker §MFA, R17). MFA zorunlu
        // yönlendirmesi şimdilik kapatıldı; operatör doğrudan konsola girer. Backend MFA
        // enforce etmiyor (platform token MFA'sız geçerli). Düzeltilince geri açılacak:
        // if (result.mfa_setup_required) { navigate('/platform/mfa-setup'); return; }
        await fetchPlatformSession();
        toast.success(t('login.toast.platformSuccess'));
        navigate('/platform');
        return;
      }

      await fetchSession();
      toast.success(t('login.toast.success'));
      navigate('/');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('login.toast.fail');
      toast.error(message);
      void setUser; // unused warn engelleme
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-custel-bg-subtle p-8">
      <div className="w-full max-w-md rounded-custel border border-custel-border bg-custel-surface p-8 shadow-sm">
        <h1 className="mb-2 text-2xl font-semibold text-custel-text">{t('login.title')}</h1>
        <p className="mb-6 text-sm text-custel-text-secondary">{t('login.subtitle')}</p>

        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label className="mb-1 block text-sm font-medium text-custel-text" htmlFor="email">
              {t('login.email')}
            </label>
            <input
              id="email"
              type="email"
              className="input-base"
              placeholder="ornek@custel.co"
              autoComplete="email"
              {...register('email')}
            />
            {errors.email && <p className="mt-1 text-xs text-custel-danger">{errors.email.message}</p>}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-custel-text" htmlFor="password">
              {t('login.password')}
            </label>
            <input
              id="password"
              type="password"
              className="input-base"
              autoComplete="current-password"
              {...register('password')}
            />
            {errors.password && <p className="mt-1 text-xs text-custel-danger">{errors.password.message}</p>}
          </div>

          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting ? t('login.submitting') : t('login.submit')}
          </button>
        </form>

        <div className="mt-6 flex justify-between text-sm">
          <Link
            to="/sifremi-unuttum"
            className="text-custel-g700 underline-offset-2 hover:text-custel-ink hover:underline"
          >
            {t('login.forgotPassword')}
          </Link>
          <Link to="/kayit" className="text-custel-g700 underline-offset-2 hover:text-custel-ink hover:underline">
            {t('login.signup')}
          </Link>
        </div>
      </div>
    </main>
  );
}
